<template>
  <div id="chat-container" :class="`${loading ? 'preload' : ''} ${bubbleOpen ? 'chat-open' : 'chat-closed'}`">
    <div v-show="bubbleOpen" id="chat-window" class="text-bg-user-primary">
      <div class="chat-pane">
        <div :class="`conversations ${opening ? 'opening-chat' : ''} ${convoOpen === true ? 'convo-open' : 'convo-closed'}`">
          <button class="close-button" @click="closeChat()">
            <i class="fas fa-window-minimize" />
          </button>
          <div class="convos-section">
            <div class="section-header">
              Channels
            </div>
            <div>
              <div v-for="(channel, index) in conversations.filter(convo => convo.id < 0)" :key="index" class="convo-button" @click="openConvo(channel.id, channel.name)">
                # {{ channel.name }}
                <span v-if="channel.unread > 0" class="unread-circle">
                  {{ channel.unread }}
                </span>
              </div>
            </div>
          </div>
          <div class="convos-section">
            <div class="section-header">
              Direct Messages
            </div>
            <div>
              <div v-for="(user, index) in conversations.filter(user => user.id >= 0)" :key="index" class="convo-button" @click="openConvo(user.id, user.name)">
                <i :class="`fa fa-circle online-circle ${user.online ? 'is-online' : 'not-online'}`" />
                <div>{{ user.name + (user.role ? ' - ' + user.role : '') }}</div>
                <span v-if="user.unread > 0" class="unread-circle">
                  {{ user.unread }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div v-if="conversation !== null" class="convo-pane">
          <div class="window-header">
            <button class="back-to-convos-button" @click="backToList()">
              <i class="fas fa-chevron-left" />
            </button>
            <span v-if="isChannel" class="convo-header-title"># {{ conversation.name }}</span>
            <i v-else :class="`fa fa-circle mr-1 ${conversation.online ? 'is-online' : 'not-online'}`"/>
            <span>
              <span v-if="!isChannel" class="convo-header-title">{{ conversation.name }}</span>
              <span v-if="conversation.role" class="convo-header-role">{{ ' - ' + conversation.role }}</span>
            </span>
            <button class="minimize-button" @click="closeChat()">
              <i class="fas fa-window-minimize" />
            </button>
          </div>
          <div v-if="isChannel" @click="openMembersModal" class="convo-header members-header">
            {{ `${numMembers} member${numMembers == 1 ? '' : 's'} in this channel` }}
          </div>
          <div class="convo-messages" ref="chat">
            <div v-for="(message, index) in processedMessages" :key="index" class="p-2">
              <span class="message-sender">{{ message.senderName }}</span>
              <span class="timestamp">{{ formatDate(message.timestamp) }}</span>
              <div>
                {{ message.message }}
              </div>
              <span v-if="message.videoUrl">
                <br>
                <video width="320" height="240" controls>
                  <source :src="message.videoUrl" type="video/mp4">
                  <track kind="captions" :src="message.captionsPath" srclang="en-us" label="English"/>
                  Your browser does not support the video tag.
                </video>
              </span>
            </div>
          </div>
          <div class="convo-input">
            <div class="typing-panel" v-show="chatbotTyping">
              <div class="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <p class="typing-text">{{ chatbotSender }} is typing...</p>
            </div>
            <b-input-group
            class="chat-input-group">
              <b-form-input
                v-model="newMessage"
                placeholder="Enter your message..."
                @keyup.enter="sendMessage()"
                class="chat-input"/>
              <b-input-group-append is-text>
                <i class="fas fa-paper-plane" @click="sendMessage()"/>
              </b-input-group-append>
            </b-input-group>
          </div>
          <b-modal id="members-modal" v-if="conversation" ok-only ok-variant="user-primary" :title="`Members of # ${conversation.name}`">
            <b-list-group>
              <b-list-group-item v-for="(member, index) in channelMembers" :key="index">
                <b-row>
                  <b-col md="auto">
                    <span class="user-initials">{{ convoInitials(member) }}</span>
                  </b-col>
                  <b-col align-self="center">
                    <span class="member-name">{{ member }}</span>
                  </b-col>
                </b-row>
              </b-list-group-item>
            </b-list-group>
          </b-modal>
        </div>
      </div>
    </div>
    <div v-show="!bubbleOpen" class="chat-bubble" @click="openChat()">
      <i id="msg-icon" class='fas fa-comment-dots'></i>
      <span v-if="unread > 0" class="float-right total-unread">
        {{ unread }}
      </span>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import Mustache from 'mustache'
import sanitizeHtml from 'sanitize-html'

export default {
  layout: 'basepcs',

  data: () => ({
    newMessage: '',
    video: '',
    groupID: '',
    conversation: null,
    conversationsLoaded: false,
    convoOpen: false,
    channels: [],
    users: [],
    chatbotTyping: false,
    chatbotSender: '',
    opening: false
  }),

  computed: {
    ...mapState({
      conversations: state => state.chat.conversations,
      user: state => state.auth.user,
      loading: state => state.chat.loading,
      auth: state => state.auth,
      typing: state => state.chat.typing,
    }),
    ...mapGetters({
      classId: 'auth/classId',
      bubbleOpen: 'chat/bubbleOpenState',
      unread: 'chat/unreadMessages'
    }),
    processedMessages: function() {
      if (this.conversation.messages) {
        return this.conversation.messages.map(message => {
          let templatedMessage = { ...message }
          let auth = this.auth
          let context = { auth }

          if (message.message) {
            templatedMessage['message'] = Mustache.render(message.message, context)
            templatedMessage['message'] = sanitizeHtml(templatedMessage['message'], {
              allowedTags: [ 'a' ],
              allowedAttributes: {
                'a': [ 'href' ]
              },
            });
          }
          else {
            templatedMessage['message'] = ''
          }

          return templatedMessage
        })
      }
      else {
        return []
      }
    },
    numMembers: function() {
      if (this.conversation.members.length === 0) {
        return 'All'
      } else {
        return this.conversation.members.length
      }
    },
    isChannel: function() {
      if (this.conversation !== null) {
        return this.conversation.id < 0
      }
    },
    baseURL: function() {
      return process.env.MIX_BASE_URL
    },
    channelMembers: function() {
      if (this.conversation !== null && this.conversation.members !== undefined) {
        if (this.conversation.members.length) {
          return this.conversation.members
        } else {
          return this.conversations.filter(convo => convo.id >= 0).map(convo => convo.name)
        }
      }
      return []
    },
  },

  watch: {
    bubbleOpen: function() {
      if (!this.bubbleOpen) {
        this.convoOpen = false
      }
    },
    conversations: function() {
      this.conversations.forEach(convo => {
        this.addUnread(convo.unread)
      })
      if (!this.conversationsLoaded && this.conversations.length > 0) {
        this.getChatData()
      }
    },
    typing() {
      if (this.typing && this.typing.length) {
        this.chatbotTyping = true
        this.chatbotSender = this.typing[0]
      } else {
        this.chatbotTyping = false
        this.chatbotSender = ''
      }
    }
  },

  created() {
    if (this.conversations.length !== 0) {
      this.getChatData()
    }
    axios.post('/api/getGroup', {
      params: {
        pcs: localStorage.getItem('pcs'),
        classId: this.classId,
      },
    })
    .then(response => {
      this.groupID = response.data

      if (this.groupID) {
        Echo.join('group.chat.' + this.groupID)
        .here(user => {
          user.forEach(user => {
            this.$store.state.chat.conversations.forEach(usr => {
              if (user.id === usr.id) {
                usr.online = true
              }
            })
          })
        })
        .joining(user => {
          this.$store.state.chat.conversations.forEach(usr => {
            if (user.id === usr.id) {
              usr.online = true
            }
          })
        })
        .leaving(user => {
          this.$store.state.chat.conversations.forEach(usr => {
            if (user.id === usr.id) {
              usr.online = false
            }
          })
        })
        .listen('MessageSent', (event) => {
          if (event.recipientid === this.user.id || event.recipientid <= 0) {
            event.currentConversation = this.conversation
            this.receiveMessage(event)
            if ((event.recipientid < 0 && this.conversation.id == event.recipientid) || (event.recipientid >= 0 && this.conversation.id === event.senderid)) { // If the message is sent to the current opened conversation
              this.scrollToBottom()
            }
          }
        })
      }
    })
  },
  updated() {
    setTimeout(() => {
      this.setLoading(false)
    }, 300)
  },
  methods: {
    ...mapActions({
      openChatState: 'chat/openBubble',
      closeChatState: 'chat/closeBubble',
      updateState: 'chat/saveState',
      triggerDialogflow: 'chat/triggerDialogflow',
      sendChatMessage: 'chat/sendMessage',
      addMessageToState: 'chat/addMessageToState',
      receiveMessage: 'chat/receiveMessage',
      setLoading: 'chat/setLoading',
      addUnread: 'chat/addUnread',
      resetUnread: 'chat/resetUnread',
    }),

    formatDate(value) {
      if (value) {
        let now = new Date()
        let then = new Date(value)
        
        
        let options = {
          hour: 'numeric',
          minute: 'numeric',
          hour12: true,
        }

        if (now.getMinutes() == then.getMinutes()) {  // less than a minute
          return 'just now'
        }

        if (now.getFullYear() != then.getFullYear()) { // different year
          options.year = 'numeric'
        }
        if (now.getDate() != then.getDate()) { // different day
          options.month = 'long'
          options.day = 'numeric'
        }
        return new Intl.DateTimeFormat('en-US', options).format(then)
      }
    },

    convoInitials: function(name) {
      let initials = name.split(' ').map(word => word[0])
      if (initials.length === 1) {
        return initials[0].toUpperCase()
      } else {
        return initials[0] + initials[initials.length - 1].toUpperCase()
      }
    },

    getChatData: function() {
      axios.post('/api/pcs/chat', {
        params: {
          pcs: localStorage.getItem('pcs'),
          group: this.groupID,
          classId: this.classId,
        },
      })
      .then(response => {
        this.users = response.data.users
        this.channels = response.data.channels
        if (this.channels.length) {
          this.conversation = this.conversations.find(convo => convo.id === this.channels[0].id)
        }
        else if (this.users.length) {
          this.conversation = this.conversations.find(convo => convo.id === this.users[0].id)
        }
        else {
          this.conversation = {}
        }
        this.conversationsLoaded = true
      })
    },

    openMembersModal: function() {
      this.$bvModal.show('members-modal')
    },

    openConvo: function(id, name) {
      this.conversation = this.conversations.find(convo => {
        return (convo.id === id && convo.name === name)
      })
      if (this.conversation) {
        this.resetUnread(this.conversation)
        this.convoOpen = true
        this.updateState()
        this.scrollToBottom()
      }
    },

    backToList: function() {
      this.convoOpen = false
      this.conversation = {}
    },

    sendMessage: function() {
      if (this.newMessage == '') {
        return
      }
      let recipient = this.conversation.name
      let recipientid = this.conversation.id

      let event = {
        message: this.newMessage,
        sender: this.user.name,
        senderid: this.user.id,
        recipient: recipient,
        recipientid: recipientid,
        timestamp: new Date(),
        video: this.video,
        currentConversation: this.conversation,
      }

      this.resetUnread(this.conversation)

      // Handle stuff on user's end (add message to user's state)

      this.addMessageToState(event)


      // Handle stuff with other people/chatbots

      if (this.conversation.chatbot) {
        this.sendMessageToChatbot()
      }
      else if (recipientid >= 0 && !this.conversation.isCharacter) {
        // sending direct message
        let active = this.checkIfOnline(recipient)
        if (active) {
          // if they are on broadcast normally
          this.broadcast(recipient, recipientid)
        } else {
          // if they aren't on
          this.sendToOffline(recipient, recipientid)
        }
      } else if (recipientid < 0) {
        // sending to channel
        this.broadcast(recipient, recipientid)
        // add message to states of users not online
        if (this.conversation.members === []) {
          // channel including all team members
          this.users.forEach((user) => {
            if (!this.checkIfOnline(user.name)) { // If recipient is not online
              this.sendToOffline(user.name, user.id, recipientid)
            }
          })
        }
      }
      this.updateState()

      this.newMessage = ''

      this.scrollToBottom()
    },

    sendMessageToChatbot: async function () {
      if (this.newMessage == "") {
        return;
      }
      
      let data = {
        message: this.newMessage,
        recipient: this.conversation.name,
        recipientid: this.conversation.id,
        returnMessages: true,
      }

      let messages = await this.triggerDialogflow(data)
      if (Array.isArray(messages)) {
        for (const message of messages) {
          await this.addMessageFromChatbot(message)
          this.scrollToBottom()
        }
      } else {
        await this.addMessageFromChatbot(messages)
        this.scrollToBottom()
      }
    },

    addMessageFromChatbot: function (message) {
      return new Promise((resolve) => {
        this.chatbotSender = message.sender
        this.chatbotTyping = true
        this.scrollToBottom()
        let delay = 0
        
        if (message.message.length <= 100) {
          delay = Math.random() + 1
        }
        else if (message.message.length <= 200) {
          delay = Math.random() + 2
        }
        else if (message.message.length <= 300) {
          delay = Math.random() + 3
        }
        else {
          delay = Math.random() + 5
        }
        let appScope = this

        setTimeout(function() {
          appScope.sendChatMessage(message)
          appScope.chatbotTyping = false
          appScope.chatbotSender = ''
          resolve()
        }, delay * 1000);
      })
    },

    scrollToBottom () {
      this.$nextTick(() => {
        this.$refs.chat.scrollTop = this.$refs.chat.scrollHeight
      })
    },

    checkIfOnline: function(recipient) {
      // check if the other user is on
      return this.conversations.find((user) => { 
        return (user.name === recipient) 
      }).online
    },

    sendToOffline: function(recipient, id) {
      axios.post('/api/messageOffline', {
        params: {
          message: this.newMessage,
          pcs: localStorage.getItem('pcs'),
          classId: this.classId,
          sender: this.user.name,
          senderid: this.user.id,
          recipient: recipient,
          recipientid: id,
          timestamp: new Date(),
          video: this.video,
        },
      })
    },

    broadcast: function(recipient, id) {
      axios.post('/api/message', {
        params: {
          message: this.newMessage,
          pcs: localStorage.getItem('pcs'),
          classId: this.classId,
          recipient: recipient,
          recipientid: id,
          name: this.user.name,
          id: this.user.id,
          timestamp: new Date(),
          all: false,
          video: this.video,
        },
      })
    },

    openChat() {
      this.opening = true
      this.openChatState()
      setTimeout(() => {this.opening = false}, 200);
    },
    closeChat() {
      this.closeChatState()
      this.conversation = {}
    },
  },
}
</script>

<style scoped lang="scss">
#chat-container{
  position: fixed;
  z-index: 9;
  background-color: var(--primary);
}

.preload {
  width: 50px;
  height: 50px;
  right: 20px;
  bottom: 15px;
  border-radius: 50%;
  animation-duration: 0s !important;
}

.chat-open {
  width: 350px;
  height: 50%;
  min-height: 400px;
  right: 10px;
  bottom: 10px;
  border-radius: 15px;
  animation: openChat 0.15s ease-in-out;
}

.chat-closed {
  width: 50px;
  height: 50px;
  min-height: 50px;
  right: 20px;
  bottom: 15px;
  border-radius: 50%;
  animation: closeChat 0.15s ease-in-out;
}

@keyframes openChat {
  0% {
    width: 50px;
    height: 50px;
    min-height: 50px;
    right: 20px;
    bottom: 15px;
    border-radius: 50%;
  }
  70% {
    width: 350px;
    height: 350px;
    right: 10px;
    bottom: 12px;
  }
  100% {
    width: 350px;
    height: 50%;
    min-height: 400px;
    right: 10px;
    bottom: 10px;
    border-radius: 15px;
  }
}

@keyframes closeChat {
  0% {
    width: 350px;
    height: 50%;
    min-height: 400px;
    right: 10px;
    bottom: 10px;
    border-radius: 15px;
  }
  30% {
    width: 350px;
    height: 350px;
    right: 20px;
    bottom: 12px;
  }
  100% {
    width: 50px;
    height: 50px;
    min-height: 50px;
    right: 20px;
    bottom: 15px;
    border-radius: 50%;
  }
}

.total-unread {
  position: absolute;
  top: -10px;
  right: -10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  color: white;
  height: 20px;
  width: 20px;
  padding: 7px;
  border-radius: 50%;
  background-color: red;
  word-break: keep-all;
  &:hover {
    cursor: default;
  }
}

#chat-window {
  display: flex;
  flex-direction: column;
  width: 350px;
  height: 100%;
  background-color: var(--primary);
  border-radius: 15px;
  box-shadow: 1px 1px 8px grey;
  font-family: 'Roboto', sans-serif;
}

// Scrollbar stuff
/* width */
::-webkit-scrollbar {
  width: 10px;
}

/* Track */
::-webkit-scrollbar-track {
  box-shadow: inset 0 0 5px grey;
  border-radius: 10px;
}

/* Handle */
::-webkit-scrollbar-thumb {
  background: lightgrey;
  border-radius: 10px;
}

.window-header {
  padding: 7px;
  display: flex;
  align-items: center;
  background-color: var(--primary);
  color: white;
  border-radius: 15px 15px 0 0;
  border-bottom: 2px solid black;
}

.back-to-convos-button {
  border: none;
  background: none;
  font-size: 20px;
}

.close-button {
  border: none;
  background: none;
  font-size: 20px;
  position: absolute;
  right: 10px;
  top: 10px;
}

.minimize-button {
  margin-left: auto;
  border: none;
  background: none;
  font-size: 20px;
}

/* Channel and Direct Conversation Select */
.chat-pane {
  border-radius: 15px;
  height: 100%;
  background-color: var(--primary);
  position: relative;
  overflow-x: hidden;
}

.conversations {
  display: flex;
  flex-flow: column;
  background-color: var(--primary);
  padding: 10px;
  border-radius: 15px;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  z-index: 100;
  overflow-y: scroll;
}

.convo-open {
  left: -350px;
  animation: slide-out 0.2s ease;
}

@keyframes slide-out {
  0% {
    left: 0;
  }
  100% {
    left: -350px;
  }
}

.opening-chat {
  animation-duration: 0s !important;
}

.convo-closed {
  left: 0px;
  animation: slide-in 0.2s ease;
}

@keyframes slide-in {
  0% {
    left: -350px;
  }
  100% {
    left: 0;
  }
}

.convos-section {
  margin-bottom: 5px;
}

.section-header {
  font-size: 18px;
  padding: 5px;
}

.convo-button {
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  word-break: break-word;
}

.user-initials {
  position: relative;
  height: 35px;
  width: 35px;
  text-align: center !important;
  vertical-align: middle !important;
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: black;
  color: white;
  padding: 20px;
  font-size: 1.2rem;
  font-family: 'Nunito', sans-serif;
  font-weight: 400 !important;
  margin-right: 5px;
}

.member-name {
  word-break: break-word;
}

.online-circle {
  font-size: 10px;
  margin-right: 5px;
}

.is-online {
  color: #07d54b;
}

.not-online {
  color: rgb(151, 151, 151);
}

.unread-circle {
  margin-left: auto;
  display: flex;
  height: 10px;
  width: 10px;
  font-size: 12px;
  color: white;
  padding: 10px;
  border-radius: 50%;
  background-color: red;
  align-items: center;
  justify-content: center;
  word-break: keep-all;
}

/* Individual Conversation pane */
.convo-pane {
  display: flex;
  flex-flow: column;
  height: 100%;
  overflow-y: auto;
  color: black;
  background-color: white;
  border-radius: 15px;
}

.convo-header {
  padding: 10px;
  border-bottom: 1px solid gray;
}

.convo-header-title {
  font-size: 18px;
  font-weight: bold;
  word-break: break-word;
}

.convo-messages {
  display: flex;
  flex-flow: column;
  flex: 1 1 0px;
  overflow-y: auto;
  word-wrap: break-word;
  padding: 5px;
}

.message-sender {
  font-weight: bold;
  margin-right: 5px;
}

.timestamp {
  font-size: 12px;
  color: gray;
}

.convo-input {
  margin-top: auto;
  border-bottom-left-radius: 15px;
  border-bottom-right-radius: 15px;
}

.chat-input-group {
  border-bottom-left-radius: 15px;
  border-bottom-right-radius: 15px;
  border-top: solid 2px black;
}

.chat-input {
  border-width: 0;
  border-bottom-left-radius: 15px;
  &:focus {
    // border-width: 0;
    box-shadow: none;
  }
}

.members-header{
  cursor: pointer;
}

.members-header:hover {
  text-decoration: underline;
}

.typing-panel {
  display: flex;
  align-items: baseline;
  padding: 5px;
}

.typing-text {
  margin-left: 5px;
  font-size: 12px;
}

.typing-indicator {
  will-change: transform;
  width: auto;
  border-radius: 10%;
  display: table;
  margin-bottom: 12px;
  position: relative;
  animation: 2s bulge infinite ease-out;
  span {
    height: 7px;
    width: 7px;
    float: left;
    margin: 0 1px;
    background-color: #9E9EA1;
    display: block;
    border-radius: 50%;
    opacity: 0.4;
    @for $i from 1 through 3 {
      &:nth-of-type(#{$i}) {
        animation: 1s blink infinite ($i * .3333s);
      }
    }
  }
}

@keyframes blink {
  50% {
    opacity: 1;
  }
}

@keyframes bulge {
  50% {
    transform: scale(1.05);
  }
}

// Chat bubble styles
.chat-bubble {
  width: 50px;
  height: 50px;
  background-color: var(--primary);
  border-radius: 50%;
  box-shadow: 1px 1px 8px grey;
  position: relative;
}

#msg-icon {
  position: relative;
  font-size: 30px;
  color: white;
  margin: 10px;
}

.input-group-text {
  background-color: white;
  border-width: 0;
  color: black;
}
</style>
